import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './UserSettings.css';

const API_URL = import.meta.env.VITE_API_URL;

/* ===============================================================
   ヘルパー関数（API境界での変換）
   =============================================================== */

/**
 * サーバーレスポンスをフロント用に正規化
 * @param {Object} row - サーバーから取得したユーザーデータ
 * @returns {Object} - 正規化されたユーザーオブジェクト
 */
const normalizeUser = (row) => {
  if (!row || typeof row !== 'object') {
    return {
      mailOnNewEntry: false,
      user_name: '',
      user_email: '',
      phone: '',
      is_admin: false,
    };
  }
  return {
    // 通知設定
    mailOnNewEntry: Boolean(row.receive_message),
    
    // プロフィール
    user_name: row.user_name ?? '',
    user_email: row.user_email ?? '',
    phone: row.phone ?? '',
    
    // 権限（is_adminのみ使用）
    is_admin:
      typeof row.is_admin === 'boolean'
        ? row.is_admin
        : (row.org_role === 'admin'),
  };
};

/**
 * プロフィールデータをサーバー送信用に変換（snake_case）
 * @param {Object} p - プロフィールオブジェクト
 * @returns {Object} - サーバー送信用オブジェクト
 */
const denormalizeProfile = (p) => {
  return {
    user_name: p?.user_name ?? '',
    user_email: p?.user_email ?? '',
    phone: p?.phone ?? '',
  };
};

/**
 * 通知設定データをサーバー送信用に変換
 * @param {Object} n - 通知設定オブジェクト
 * @returns {Object} - サーバー送信用オブジェクト
 */
const denormalizeNotify = (n) => {
  return {
    receive_message: Boolean(n?.mailOnNewEntry),
  };
};

/* ===============================================================
   メインコンポーネント
   =============================================================== */

const UserSettings = () => {
  /* ---------------------------------------------------------------
     State定義
     --------------------------------------------------------------- */
  
  // プロフィール関連（初期値はsessionStorageを尊重）
  const [profile, setProfile] = useState({
    user_name: sessionStorage.getItem('WuserName') || '',
    user_email: sessionStorage.getItem('WuserAddress') || '',
    phone: '',
  });
  const [pfLoading, setPfLoading] = useState(true);
  const [pfSaving, setPfSaving] = useState(false);
  const [pfMsg, setPfMsg] = useState('');

  // 通知設定関連
  const [notify, setNotify] = useState({
    mailOnNewEntry: false,
  });
  const [ntSaving, setNtSaving] = useState(false);
  const [ntMsg, setNtMsg] = useState('');

  // パスワード変更関連
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [showPw, setShowPw] = useState({
    current: false,
    next: false,
    confirm: false,
  });

  // 権限（管理者スイッチ）関連
  const [admin, setAdmin] = useState({ isAdmin: false });
  const [adSaving, setAdSaving] = useState(false);
  const [adMsg, setAdMsg] = useState('');

  /* ---------------------------------------------------------------
     API呼び出し関数
     --------------------------------------------------------------- */
  
  /**
   * ユーザーデータを取得
   */
  const fetchUserData = async () => {
    setPfLoading(true);
    try {
      
      const res = await axios.get(`${API_URL}/api/userdata/`);

      if (!res.data?.success) throw new Error(res.data?.error || 'failed');

      const raw = res.data.data;
      const row = Array.isArray(raw) ? raw[0] : raw;
      const u = normalizeUser(row);

      // 通知設定を反映
      setNotify((prev) => ({
        ...prev,
        mailOnNewEntry: u.mailOnNewEntry,
      }));

      // プロフィールを反映（空の場合は既存sessionStorage初期値を維持）
      setProfile((prev) => ({
        user_name: u.user_name?.trim() ? u.user_name : prev.user_name,
        user_email: u.user_email?.trim() ? u.user_email : prev.user_email,
        phone: u.phone ?? (prev.phone ?? ''),
      }));

      // 権限を反映（is_adminのみ）
      setAdmin({ isAdmin: Boolean(u.is_admin) });
    } catch {
      // 必要ならメッセージ表示
    } finally {
      setPfLoading(false);
    }
  };

  /**
   * プロフィールを保存
   */
  const saveProfile = async () => {
    setPfMsg('');
    setPfSaving(true);
    
    try {
      const payload = { profile: denormalizeProfile(profile) };
      const res = await axios.put(`${API_URL}/api/saveProfile/`, payload);

      if (res.data?.success) {
        setPfMsg(res.data.message || 'プロフィールを保存しました。');
        // sessionStorageも更新
        try {
          sessionStorage.setItem('WuserName', profile.user_name || '');
          sessionStorage.setItem('WuserAddress', profile.user_email || '');
        } catch {}
      } else {
        setPfMsg(res.data?.message || 'プロフィールの保存に失敗しました。');
      }
    } catch (err) {
      setPfMsg('プロフィールの保存に失敗しました（通信エラー）');
    } finally {
      setPfSaving(false);
      setTimeout(() => setPfMsg(''), 2500);
    }
  };

  /**
   * 通知設定を保存
   */
  const saveNotify = async () => {
    setNtMsg('');
    setNtSaving(true);

    try {
      const payload = { notify: denormalizeNotify(notify) };
      await axios.put(`${API_URL}/api/saveNotify/`, payload);
      setNtMsg('通知設定を保存しました。');
    } catch (err) {
      setNtMsg(
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        '通知設定の保存に失敗しました。'
      );
    } finally {
      setNtSaving(false);
      setTimeout(() => setNtMsg(''), 2500);
    }
  };

  /**
   * パスワードを変更
   */
  const changePassword = async () => {
    setPwMsg('');
    
    // バリデーション
    if (!pwd.next || !pwd.confirm) {
      setPwMsg('新しいパスワードを入力してください。');
      return;
    }
    if (pwd.next !== pwd.confirm) {
      setPwMsg('新しいパスワードが一致しません。');
      return;
    }
    if (pwd.next.length < 8) {
      setPwMsg('新しいパスワードは8文字以上にしてください。');
      return;
    }

    setPwSaving(true);

    try {
      await axios.post(
        `${API_URL}/api/changePassword/`,
        { current: pwd.current, next: pwd.next, confirm: pwd.confirm }
      );

      setPwMsg('パスワードを変更しました。');
      setPwd({ current: '', next: '', confirm: '' });
    } catch (err) {
      setPwMsg(
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        'パスワードの変更に失敗しました。'
      );
    } finally {
      setPwSaving(false);
      setTimeout(() => setPwMsg(''), 2500);
    }
  };

  /**
   * 管理者機能を有効化（API呼び出し）
   */
  const requestAdminEnable = async () => {
    setAdMsg('');
    setAdSaving(true);
    
    try {
      const payload = { is_admin: true };
      const res = await axios.put(`${API_URL}/api/userRole/`, payload );

      const success = Boolean(res.data?.success);
      const confirmed =
        typeof res.data?.is_admin === 'boolean' ? res.data.is_admin : true;

      if (success && confirmed) {
        // 成功：ロック（以降は触れない）
        setAdmin({ isAdmin: true });
        setAdMsg(res.data?.message || '再度ログインしてご利用ください。');
      } else {
        // 失敗：スイッチを元に戻す
        setAdmin({ isAdmin: false });
        setAdMsg(res.data?.message || '機能追加に失敗しました。');
      }
    } catch (err) {
      setAdmin({ isAdmin: false });
      setAdMsg(
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        '機能追加に失敗しました。'
      );
    } finally {
      setAdSaving(false);
    }
  };

  /* ---------------------------------------------------------------
     イベントハンドラ
     --------------------------------------------------------------- */
  
  /**
   * 管理者スイッチ変更時のハンドラ
   * @param {Event} e - イベントオブジェクト
   */
  const onAdminSwitchChange = async (e) => {
    const wantOn = e.target.checked;

    // 既に管理者なら操作不可（disabledのはずだが二重ガード）
    if (admin.isAdmin) return;

    if (wantOn) {
      const ok = window.confirm('機能の追加を行いますか？\nこの操作は取り消せません。');
      if (!ok) return;

      // 先に見た目をONにし、API成否に応じて確定/巻き戻し
      setAdmin({ isAdmin: true });
      await requestAdminEnable();
    } else {
      // OFF方向は禁止（ロールダウン不可の仕様）
      alert('機能追加は解除できません。');
    }
  };

  /**
   * 管理者スイッチクリック時のハンドラ
   */
  const handleAdminSwitchClick = () => {
    if (adSaving || admin.isAdmin) return;
    onAdminSwitchChange({ target: { checked: true }, preventDefault() {} });
  };

  /**
   * 管理者スイッチキーダウン時のハンドラ
   * @param {KeyboardEvent} e - キーボードイベント
   */
  const handleAdminSwitchKeyDown = (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (adSaving || admin.isAdmin) return;
      onAdminSwitchChange({ target: { checked: true }, preventDefault() {} });
    }
  };

  /* ---------------------------------------------------------------
     useEffect（副作用処理）
     --------------------------------------------------------------- */
  
  // 初回ロード時にユーザーデータを取得
  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------------------------------------------------------
     レンダリング関数
     --------------------------------------------------------------- */
  
  /**
   * プロフィールセクションを表示
   */
  const renderProfileSection = () => (
    <section className="h01-section">
      <div className="h01-sec__hd">
        <h3>プロフィール</h3>
        {pfMsg && <span className="h01-msg">{pfMsg}</span>}
      </div>

      {pfLoading ? (
        <div className="h01-loading">読み込み中…</div>
      ) : (
        <div className="h01-form">
          {/* 表示名 */}
          <div className="h01-field">
            <label htmlFor="pf-name">
              表示名 <span className="h01-required">必須</span>
            </label>
            <input
              id="pf-name"
              type="text"
              value={profile.user_name}
              onChange={(e) => setProfile({ ...profile, user_name: e.target.value })}
              required
            />
          </div>

          {/* メールアドレス */}
          <div className="h01-field">
            <label htmlFor="pf-mail">
              メールアドレス <span className="h01-required">必須</span>
            </label>
            <input
              id="pf-mail"
              type="email"
              value={profile.user_email}
              onChange={(e) => setProfile({ ...profile, user_email: e.target.value })}
              required
            />
          </div>

          {/* 電話番号 */}
          <div className="h01-field">
            <label htmlFor="pf-phone">
              電話番号 <span className="h01-required">任意</span>
            </label>
            <input
              id="pf-phone"
              type="phone"
              value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
            />
          </div>

          <div className="h01-actions">
            <button
              className="h01-btn h01-btn--primary"
              onClick={saveProfile}
              disabled={pfSaving}
            >
              {pfSaving ? '保存中…' : '保存'}
            </button>
          </div>
        </div>
      )}
    </section>
  );

  /**
   * パスワード入力フィールドを表示
   * @param {string} id - 入力フィールドのID
   * @param {string} label - ラベルテキスト
   * @param {string} field - pwdオブジェクトのフィールド名
   * @param {string} autoComplete - autocomplete属性値
   * @param {boolean} readOnlyFocus - フォーカス時にreadOnlyを解除するか
   */
  const renderPasswordField = (id, label, field, autoComplete, readOnlyFocus = false) => (
    <div className="h01-field h01-field--pw">
      <label htmlFor={id}>{label}</label>
      <div className="h01-pw-input">
        <input
          id={id}
          name={readOnlyFocus ? 'cur_pw_input' : undefined}
          type={showPw[field] ? 'text' : 'password'}
          value={pwd[field]}
          onChange={(e) => setPwd({ ...pwd, [field]: e.target.value })}
          autoComplete={autoComplete}
          readOnly={readOnlyFocus || undefined}
          onFocus={readOnlyFocus ? (e) => e.currentTarget.removeAttribute('readOnly') : undefined}
        />
        <button
          type="button"
          className="h01-pw-toggle"
          onClick={() => setShowPw((s) => ({ ...s, [field]: !s[field] }))}
          aria-label={showPw[field] ? 'パスワードを非表示' : 'パスワードを表示'}
        >
          {showPw[field] ? '隠す' : '表示'}
        </button>
      </div>
    </div>
  );

  /**
   * パスワード変更セクションを表示
   */
  const renderPasswordSection = () => (
    <section className="h01-section">
      <div className="h01-sec__hd">
        <h3>パスワード変更</h3>
        {pwMsg && <span className="h01-msg">{pwMsg}</span>}
      </div>
      <div className="h01-form">
        {/* 現在のパスワード */}
        {renderPasswordField('pw-cur', '現在のパスワード', 'current', 'off', true)}

        {/* 新しいパスワード */}
        {renderPasswordField('pw-new', '新しいパスワード', 'next', 'new-password')}

        {/* 新しいパスワード（確認） */}
        {renderPasswordField('pw-cf', '新しいパスワード（確認）', 'confirm', 'new-password')}

        <div className="h01-actions">
          <button className="h01-btn" onClick={changePassword} disabled={pwSaving}>
            {pwSaving ? '変更中…' : '変更する'}
          </button>
        </div>
      </div>
    </section>
  );

  /**
   * 通知設定セクションを表示
   */
  const renderNotifySection = () => (
    <section className="h01-section">
      <div className="h01-sec__hd">
        <h3>通知設定</h3>
        {ntMsg && <span className="h01-msg">{ntMsg}</span>}
      </div>
      <div className="h01-form">
        <label className="h01-check">
          <input
            type="checkbox"
            checked={notify.mailOnNewEntry}
            onChange={(e) => setNotify({ ...notify, mailOnNewEntry: e.target.checked })}
          />
          配信メッセージを受け取る
        </label>

        <div className="h01-actions">
          <button className="h01-btn" onClick={saveNotify} disabled={ntSaving}>
            {ntSaving ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </section>
  );

  /**
   * 機能追加（管理者スイッチ）セクションを表示
   */
  const renderAdminSection = () => (
    <section className="h01-section">
      <div className="h01-sec__hd">
        <h3>機能追加</h3>
        {adMsg && <span className="h01-msg">{adMsg}</span>}
      </div>

      <div className="h01-form">
        {/* 管理者スイッチ */}
        <div className="h01-switch-row">
          <button
            id="admin-switch"
            type="button"
            role="switch"
            aria-checked={admin.isAdmin}
            aria-disabled={adSaving || admin.isAdmin || undefined}
            className={[
              'h01-switch',
              admin.isAdmin ? 'h01-switch--on' : 'h01-switch--off',
              adSaving || admin.isAdmin ? 'h01-switch--disabled' : '',
            ].join(' ')}
            onClick={handleAdminSwitchClick}
            onKeyDown={handleAdminSwitchKeyDown}
          >
            <span className="h01-switch__knob" />
          </button>
          <label htmlFor="admin-switch" className="h01-switch__label">
            イベント作成可能にする
          </label>
        </div>

        <p id="admin-desc" className="h01-help">
          管理者としてイベント作成可能となります。一度追加すると解除はできません。
        </p>
      </div>
    </section>
  );

  /* ---------------------------------------------------------------
     メインレンダリング
     --------------------------------------------------------------- */
  return (
    <div className="h01-wrap">
      <h2 className="h01-page-title">個人設定</h2>

      {/* プロフィール */}
      {renderProfileSection()}

      {/* パスワード変更 */}
      {renderPasswordSection()}

      {/* 通知設定 */}
      {/* {renderNotifySection()} */}

      {/* 機能追加（管理者スイッチ） */}
      {/* {renderAdminSection()} */}
    </div>
  );
};

export default UserSettings;